import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { stringToHexColor } from '../../shared/color.utils';
import { SearchReturnType } from '../../shared/models/search/search-query.model';
import { SearchSuggestionResponse } from '../../shared/models/search/search-suggestions.model';
import { replayValue } from '../../shared/observable.utils';
import { CommonModule } from '@angular/common';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { SessionModule } from 'app/src/sessions/session.module';
import { PipesModule } from 'app/src/shared/pipes.module';

export interface TagFilter {
  contains: string[]
  excludes: string[]
}

@Component({
  host: { 'test-id': 'tag-select-menu' },
  selector: 'tag-select-menu',
  templateUrl: './tag-select-menu.component.html',
  styles: [`
    button.full-width {
      width: 100%;
      margin: 0 !important;
    }
  `],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    PipesModule,
    ReactiveFormsModule,
    MaterialModule,
    SessionModule,
  ],
})
export class TagSelectMenuComponent implements OnChanges {
  @Input() value: TagFilter
  @Input() resultType: SearchReturnType

  @Output() valueChange = new EventEmitter<TagFilter>()

  @ViewChild('typeInputElementContains') typeInputElementContains: ElementRef<HTMLInputElement>
  @ViewChild('typeInputElementExcludes') typeInputElementExcludes: ElementRef<HTMLInputElement>

  suggestedContainsTags = []
  suggestedExcludesTags = []

  form = new FormGroup({
    contains: new FormControl([]),
    containsTagInput: new FormControl(''),
    excludes: new FormControl([]),
    excludesTagInput: new FormControl(''),
  });

  constructor(
    private coreApi: CoreApiService,
  ) {
    replayValue(this.form.get('containsTagInput'), { debounce: 200 }).pipe(
      filter(() => Boolean(this.value && this.resultType)),
      switchMap(input => this.getSuggestions(input)),
    ).subscribe(response => {
      const usedTags = new Set([...this.value.contains, ...this.value.excludes]);
      this.suggestedContainsTags = response.suggestions.map(suggestion => suggestion.value).filter(suggestion => !usedTags.has(suggestion));
    });
    replayValue(this.form.get('excludesTagInput'), { debounce: 200 }).pipe(
      filter(() => Boolean(this.value && this.resultType)),
      switchMap(input => this.getSuggestions(input)),
    ).subscribe(response => {
      const usedTags = new Set([...this.value.contains, ...this.value.excludes]);
      this.suggestedExcludesTags = response.suggestions.map(suggestion => suggestion.value).filter(suggestion => !usedTags.has(suggestion));
    });
    combineLatest([
      replayValue(this.form.controls.contains),
      replayValue(this.form.controls.excludes),
    ]).pipe(
      map(([contains, excludes]) => ({ contains, excludes })),
    ).subscribe(this.valueChange);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value.currentValue !== changes.value.previousValue) {
      this.form.setValue({
        contains: changes.value.currentValue.contains,
        containsTagInput: '',
        excludes: changes.value.currentValue.excludes,
        excludesTagInput: '',
      }, { emitEvent: false }); // suppressing events to prevent infinite value change loop
    }
  }

  private getSuggestions(tags: string): Observable<SearchSuggestionResponse> {
    return tags.length >= 3 // suggestions require at least 3 characters
      ? this.coreApi.dataexplorer.suggest({ structured_query: `${this.resultType}.tags = ${tags}` })
      : of({ from: 0, suggestions: [] });
  }

  autoCompleteSelected(destList: string, event: MatAutocompleteSelectedEvent): void {
    const { contains, excludes } = this.value;
    if (destList === 'contains') {
      if (excludes.includes(event.option.value)) {
        return;
      }
      contains.push(event.option.value);
      this.form.controls.contains.setValue(contains);
      this.typeInputElementContains.nativeElement.value = '';
    }
    else if (destList === 'excludes') {
      if (contains.includes(event.option.value)) {
        return;
      }
      excludes.push(event.option.value);
      this.form.controls.excludes.setValue(excludes);
      this.typeInputElementExcludes.nativeElement.value = '';
    }
  }

  containsTagDelete(tagIndex: number): void {
    const tags = this.form.controls.contains.value;
    tags.splice(tagIndex, 1);
    this.form.controls.contains.setValue(tags);
  }

  excludeTagDelete(tagIndex: number): void {
    const tags = this.form.controls.excludes.value;
    tags.splice(tagIndex, 1);
    this.form.controls.excludes.setValue(tags);
  }

  getTagColor(tag: string): string {
    return '#' + stringToHexColor(tag);
  }

  reset(): void {
    this.typeInputElementContains.nativeElement.value = '';
    this.typeInputElementExcludes.nativeElement.value = '';
    this.form.setValue({ contains: [], excludes: [], containsTagInput: '', excludesTagInput: '' });
  }

  menuClosed(): void {
    this.typeInputElementContains.nativeElement.value = '';
    this.typeInputElementExcludes.nativeElement.value = '';
  }
}
